import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X } from 'lucide-react';
import { useAppNav } from '@/hooks/useAppNav';
import BrandMark from '@/components/BrandMark';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { navItems, showSidebar } = useAppNav();

  if (!showSidebar) {
    return null;
  }

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1 flex-col bg-background pb-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onClose}
                      className="ml-1 flex h-10 w-10 items-center justify-center"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  </div>
                </Transition.Child>

                <div className="border-b border-border px-6 py-4">
                  <BrandMark />
                </div>

                <ScrollArea className="flex-1 py-2">
                  <nav className="grid gap-1 px-4 pt-8">
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium hover:bg-accent transition-colors',
                          pathname === item.href ? 'bg-accent text-primary' : 'text-foreground/60'
                        )}
                        onClick={onClose}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </nav>
                </ScrollArea>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden border-r border-white/[0.05] bg-[#0f1117] lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:w-64 lg:flex-col">
        <div className="border-b border-white/[0.05] px-6 py-4">
          <BrandMark />
        </div>

        <ScrollArea className="flex-1 py-2">
          <nav className="grid gap-1 px-4 pt-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium hover:bg-accent transition-colors',
                  pathname === item.href ? 'bg-accent text-primary' : 'text-foreground/60'
                )}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
        </ScrollArea>
      </div>
    </>
  );
};

export default Sidebar;
